import React, { useState } from 'react';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Pencil,
  FileText,
  User,
  Image as ImageIcon,
  CheckCircle2,
  Download,
  ExternalLink,
  Printer,
  Sparkles,
  Layers,
  Scissors,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trpcClient } from '@/lib/trpc';
import { formatDate } from '@/lib/utils';
import { StandardDetailLayout } from '@/layouts/StandardDetailLayout';
import { EntityLink } from '@/components/shared/EntityLink';

const DESIGN_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Chờ xử lý', color: 'bg-amber-50 text-amber-800 border-amber-200' },
  DESIGNING: { label: 'Đang thiết kế', color: 'bg-blue-50 text-blue-800 border-blue-200' },
  REVIEWING: { label: 'Chờ duyệt KCS', color: 'bg-amber-50 text-amber-800 border-amber-200' },
  APPROVED: { label: 'Đã phê duyệt', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  REJECTED: { label: 'Từ chối / Sửa lại', color: 'bg-rose-50 text-rose-800 border-rose-200' },
};

export default function DesignView({
  designId: propId,
}: {
  designId?: string;
} = {}) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as any;
  const designId = propId || params.designId;
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');

  const {
    data: d,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['design', designId],
    queryFn: async () => (trpcClient as any).designs.getById.query({ id: designId }),
    enabled: !!designId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: 'PENDING' | 'DESIGNING' | 'REVIEWING' | 'APPROVED' | 'REJECTED') =>
      (trpcClient as any).designs.updateStatus.mutate({
        id: designId,
        status: newStatus,
      }),
    onSuccess: (_, newStatus) => {
      toast.success(`Đã cập nhật trạng thái thiết kế thành "${DESIGN_STATUS_CONFIG[newStatus]?.label}"`);
      queryClient.invalidateQueries({ queryKey: ['design', designId] });
      queryClient.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Không thể cập nhật trạng thái');
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-500 text-sm">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
        Đang tải hồ sơ thiết kế kỹ thuật bao bì...
      </div>
    );
  }

  if (error || !d) {
    return (
      <div className="p-8 text-center text-red-500">
        Không tìm thấy hồ sơ thiết kế #{designId}
      </div>
    );
  }

  const statusCfg = DESIGN_STATUS_CONFIG[d.status] || {
    label: d.status,
    color: 'bg-slate-100 text-slate-700 border-slate-300',
  };

  const product = d.product;
  const customer = product?.customer;

  return (
    <StandardDetailLayout
      entityType="design"
      entityId={d.id}
      title={`${product?.productCode || d.id}`}
      status={
        <Badge variant="outline" className={`font-semibold border text-xs px-2.5 py-0.5 ${statusCfg.color}`}>
          {statusCfg.label}
        </Badge>
      }
      breadcrumbs={[{ label: "Thiết kế", to: "/designs" }]}
      actions={
        <div className="flex items-center gap-1.5">
          {/* Đổi trạng thái nhanh */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs font-medium cursor-pointer">
                Đổi trạng thái
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => updateStatusMutation.mutate('PENDING')} className="text-xs">
                Chờ xử lý
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateStatusMutation.mutate('DESIGNING')} className="text-xs">
                Đang thiết kế
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateStatusMutation.mutate('REVIEWING')} className="text-xs">
                Chờ duyệt KCS
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateStatusMutation.mutate('APPROVED')} className="text-xs font-semibold text-emerald-600">
                Phê duyệt (Đạt)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateStatusMutation.mutate('REJECTED')} className="text-xs font-semibold text-rose-600">
                Từ chối / Yêu cầu sửa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sửa */}
          <Button
            size="sm"
            variant="default"
            className="h-7 text-xs cursor-pointer"
            onClick={() => navigate({ to: `/designs/${d.id}/edit` as any })}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />
            Sửa
          </Button>

          {/* In phiếu */}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs cursor-pointer"
            onClick={() => window.print()}
          >
            <Printer className="w-3.5 h-3.5 mr-1" />
            In Phiếu
          </Button>
        </div>
      }
    >
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Banner tiến trình */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-white p-3.5 rounded-lg border border-slate-200 shadow-2xs">
          <div>
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block">Sản phẩm</span>
            <span className="text-xs font-semibold text-slate-900 block break-words whitespace-normal mt-0.5">
              <EntityLink entityType="product" id={product?.id} code={product?.productCode} label={product?.productName} />
            </span>
          </div>
          <div>
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block">Khách hàng</span>
            <span className="text-xs font-semibold text-slate-900 block break-words whitespace-normal mt-0.5">
              <EntityLink entityType="customer" id={customer?.id} code={customer?.code} label={customer?.name} />
            </span>
          </div>
          <div>
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block">Phân loại</span>
            <span className="text-xs font-semibold text-slate-900 block mt-0.5">
              {d.designType === 'NEW' ? 'Thiết kế mới' : 'Chỉnh sửa file'}
            </span>
          </div>
          <div>
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block">Người phụ trách</span>
            <span className="text-xs font-semibold text-indigo-700 block mt-0.5">
              {d.assignee?.id ? (
                <EntityLink entityType="user" id={d.assignee.userId || d.assignee.id} label={d.assignee.fullName} />
              ) : (
                'Chưa phân công'
              )}
            </span>
          </div>
        </div>

        {/* Nội dung chi tiết */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
          <TabsList>
            <TabsTrigger value="overview">Thông tin chung</TabsTrigger>
            <TabsTrigger value="files">File Artwork & Bản vẽ</TabsTrigger>
            <TabsTrigger value="product-specs">Kỹ thuật sản phẩm</TabsTrigger>
          </TabsList>

          {/* Tab 1: Overview */}
          <TabsContent value="overview" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-4 border-b border-slate-100">
                <CardTitle className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-indigo-600" />
                  Yêu Cầu & Ghi Chú Kỹ Thuật
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="text-xs leading-relaxed text-slate-700 whitespace-pre-wrap bg-slate-50 p-3.5 rounded-md border border-slate-100">
                  {d.description || 'Chưa có ghi chú chi tiết cho yêu cầu này.'}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 text-xs">
                  <div>
                    <span className="text-slate-500">Số lượng mẫu mockup cần in:</span>{' '}
                    <span className="font-semibold text-slate-900">{d.quantity || 1} mẫu</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Người tạo yêu cầu:</span>{' '}
                    <span className="font-semibold text-slate-900">{d.requester?.fullName || 'Hệ thống'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Files */}
          <TabsContent value="files" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-4 border-b border-slate-100">
                <CardTitle className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                  Đường Dẫn Tài Liệu & Artwork
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {d.filePaths && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200 text-xs">
                    <span className="break-words whitespace-normal text-slate-700 font-mono">
                      {Array.isArray(d.filePaths) ? d.filePaths.join(', ') : String(d.filePaths)}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs shrink-0 ml-2"
                      onClick={() => {
                        const link = Array.isArray(d.filePaths) ? d.filePaths[0] : String(d.filePaths);
                        if (link.startsWith('http')) window.open(link, '_blank');
                        else toast.warning(`Đường dẫn nội bộ: ${link}`);
                      }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Mở liên kết
                    </Button>
                  </div>
                )}

                {(!d.filePaths || (Array.isArray(d.filePaths) && d.filePaths.length === 0)) && (
                  <div className="text-center py-8 text-xs text-slate-400">
                    Chưa có tệp artwork đính kèm nào được tải lên.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Kỹ thuật sản phẩm */}
          <TabsContent value="product-specs" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-4 border-b border-slate-100">
                <CardTitle className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-indigo-600" />
                  Chi Tiết Cấu Trúc Bao Bì
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-2.5 bg-slate-50 rounded border border-slate-100">
                    <span className="text-slate-500 block text-[11px]">Khuôn bế liên kết:</span>
                    <span className="font-semibold text-slate-900 mt-0.5 block">
                      {product?.dieCuts?.length > 0 ? `${product.dieCuts.length} khuôn bế` : 'Chưa gán khuôn'}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded border border-slate-100">
                    <span className="text-slate-500 block text-[11px]">Kẽm in CTP:</span>
                    <span className="font-semibold text-slate-900 mt-0.5 block">
                      {product?.plates?.length > 0 ? `${product.plates.length} bản kẽm` : 'Chưa gán kẽm'}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded border border-slate-100">
                    <span className="text-slate-500 block text-[11px]">Thông số kỹ thuật:</span>
                    <span className="font-semibold text-slate-900 mt-0.5 block">
                      {product?.technicalSpecs?.length > 0 ? 'Đã thiết lập TCKT' : 'Chưa lập TCKT'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </StandardDetailLayout>
  );
}
